import { useEffect, useState } from "react";
import { format } from "date-fns";
import { useListInventoryAuditLog } from "@workspace/api-client-react";
import type { InventoryAuditLog } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ListPagination } from "@/components/common/list-pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { catId, venId, itmId, memId, plnId, vipId } from "@/lib/display-id";

const ACTION_COLORS: Record<string, string> = {
  create: "bg-green-100 text-green-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-red-100 text-red-700",
  restore: "bg-amber-100 text-amber-700",
};

const TABLE_OPTIONS = [
  { value: "inventory_categories", label: "Categories" },
  { value: "inventory_subcategories", label: "Subcategories" },
  { value: "inventory_vendors", label: "Vendors" },
  { value: "inventory_items", label: "Items" },
  { value: "vendor_item_pricing", label: "Pricing" },
  { value: "inventory_plans", label: "Plans" },
  { value: "plan_entitlements", label: "Entitlements" },
  { value: "members", label: "Members" },
  // === Phase 2 (D-29) — Member_Entitlements + Member_Charges + Daily_Order_Schedule DO get audit-logged ===
  { value: "member_entitlements", label: "Member Entitlements" },
  { value: "member_charges", label: "Member Charges" },
  { value: "daily_order_schedule", label: "Daily Order Schedule" },
  // D-28: stock_in / stock_out NOT added — stock movements ARE the audit trail
  // === Phase 2.1 (D-33) — junction-table mutations are auditable ===
  { value: "category_charging_models", label: "Category Charging Models" },
  { value: "vendor_spaces", label: "Vendor Spaces" },
  // === Phase 23-07 (AUD23-01) — new account-module tables; members entry preserved for D-10 ===
  // D-10: historical "members" entry kept so old audit-log rows remain filterable
  { value: "accounts", label: "Accounts" },
  { value: "contacts", label: "Contacts" },
  { value: "memberships", label: "Memberships" },
  { value: "documents", label: "Documents" },
];

function getDisplayId(tableName: string, recordId: number): string {
  switch (tableName) {
    case "inventory_categories": return catId(recordId);
    case "inventory_vendors": return venId(recordId);
    case "inventory_items": return itmId(recordId);
    case "members": return memId(recordId);
    case "inventory_plans": return plnId(recordId);
    case "vendor_item_pricing": return vipId(recordId);
    default: return String(recordId);
  }
}

function ExpandableJson({ label, data }: { label: string; data: Record<string, unknown> | null | undefined }) {
  if (!data) return null;
  return (
    <div className="mt-2">
      <p className="text-xs font-semibold text-muted-foreground mb-1">{label}</p>
      <pre className="text-xs bg-muted rounded p-2 overflow-auto max-h-48">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

function AuditLogRow({ row }: { row: InventoryAuditLog }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
          {format(new Date(row.at), "MMM d, yyyy HH:mm")}
        </TableCell>
        <TableCell className="text-sm">{row.tableName.replace(/inventory_|_/g, (m) => m === "inventory_" ? "" : " ").trim()}</TableCell>
        <TableCell className="font-mono text-xs">{getDisplayId(row.tableName, row.recordId)}</TableCell>
        <TableCell>
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ACTION_COLORS[row.action] ?? ""}`}>
            {row.action}
          </span>
        </TableCell>
        <TableCell className="text-sm">
          {row.actorUserId != null ? `User #${row.actorUserId}` : "—"}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {expanded ? "Click to collapse" : "Click to expand"}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/30 px-6 py-3">
            {row.action !== "create" && (
              <ExpandableJson label="Before" data={row.before} />
            )}
            {row.action !== "delete" && (
              <ExpandableJson label="After" data={row.after} />
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function AuditLogTab() {
  const [tableName, setTableName] = useState<string | undefined>(undefined);
  const [action, setAction] = useState<"create" | "update" | "delete" | "restore" | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const { data: logsPage, isLoading } = useListInventoryAuditLog(
    { tableName, action, page, pageSize },
    { query: { queryKey: ["/api/inventory/audit-log", tableName, action, page, pageSize] } }
  );
  const logs = logsPage?.items ?? [];
  const total = logsPage?.total ?? 0;

  // Reset to page 1 when filters change.
  useEffect(() => {
    setPage(1);
  }, [tableName, action, pageSize]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <Select
          value={tableName ?? "all"}
          onValueChange={(v) => setTableName(v === "all" ? undefined : v)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All tables" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tables</SelectItem>
            {TABLE_OPTIONS.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={action ?? "all"}
          onValueChange={(v) => setAction(v === "all" ? undefined : v as typeof action)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
            <SelectItem value="restore">Restore</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            Audit Log{" "}
            <span className="text-muted-foreground font-normal text-sm">
              {total > 0 ? `(${total} events)` : ""}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Table</TableHead>
                <TableHead>Record ID</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(6)].map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-24" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    No audit events yet. Events appear here as you make changes to master data.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((row) => <AuditLogRow key={row.id} row={row} />)
              )}
            </TableBody>
          </Table>
          {total > 0 ? (
            <div className="px-4">
              <ListPagination
                total={total}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                isLoading={isLoading}
                pageSizeOptions={[25, 50, 100, 200]}
                noun={["event", "events"]}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
